import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useJobs } from '../contexts/JobContext';
import { JobCard } from '../components/JobCard';
import { Button } from '../components/Button';
import { PlusIcon, UserIcon, LogOutIcon, BellIcon, ZapIcon, ShieldIcon } from 'lucide-react';
import { motion } from 'framer-motion';
export function CustomerDashboard() {
  const {
    user,
    logout,
    updateUser
  } = useAuth();
  const {
    getJobsByCustomer
  } = useJobs();
  const navigate = useNavigate();
  const myJobs = user ? getJobsByCustomer(user.id) : [];
  const activeJobs = myJobs.filter(j => j.status !== 'paid');
  const completedJobs = myJobs.filter(j => j.status === 'paid');
  const switchToAdmin = () => {
    if (updateUser) {
      updateUser({
        role: 'admin'
      });
      navigate('/admin');
    }
  };
  return <div className="min-h-screen bg-slate-50" data-id="element-405">
      {/* Header */}
      <motion.div initial={{
      opacity: 0,
      y: -20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.4,
      ease: [0.22, 1, 0.36, 1]
    }} className="bg-gradient-to-r from-slate-900 to-slate-800 text-white p-6 pb-8" data-id="element-406">
        <div className="flex items-center justify-between mb-6" data-id="element-407">
          <div data-id="element-408">
            <motion.h1 initial={{
            opacity: 0,
            x: -10
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.1,
            duration: 0.4
          }} className="text-2xl font-bold mb-1" data-id="element-409">
              Welcome back,
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            x: -10
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.15,
            duration: 0.4
          }} className="text-slate-300" data-id="element-410">
              {user?.name}
            </motion.p>
          </div>
          <div className="flex items-center gap-3" data-id="element-411">
            <motion.button whileTap={{
            scale: 0.9
          }} className="p-2 hover:bg-slate-700 rounded-lg transition-colors relative" data-id="element-412">
              <BellIcon className="w-6 h-6" data-id="element-413" />
              <motion.span initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              delay: 0.5,
              type: 'spring',
              stiffness: 500,
              damping: 15
            }} className="absolute top-1 right-1 w-2 h-2 bg-orange-500 rounded-full" data-id="element-414" />
            </motion.button>
            <motion.button whileTap={{
            scale: 0.9
          }} onClick={() => navigate('/profile')} className="p-2 hover:bg-slate-700 rounded-lg transition-colors" data-id="element-415">
              <UserIcon className="w-6 h-6" data-id="element-416" />
            </motion.button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4" data-id="element-417">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.2,
          duration: 0.4,
          ease: [0.22, 1, 0.36, 1]
        }} className="bg-slate-800 rounded-xl p-4" data-id="element-418">
            <p className="text-slate-400 text-sm mb-1" data-id="element-419">Active Jobs</p>
            <motion.p initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3,
            duration: 0.4
          }} className="text-3xl font-bold" data-id="element-420">
              {activeJobs.length}
            </motion.p>
          </motion.div>
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.25,
          duration: 0.4,
          ease: [0.22, 1, 0.36, 1]
        }} className="bg-slate-800 rounded-xl p-4" data-id="element-421">
            <p className="text-slate-400 text-sm mb-1" data-id="element-422">Completed</p>
            <motion.p initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.35,
            duration: 0.4
          }} className="text-3xl font-bold" data-id="element-423">
              {completedJobs.length}
            </motion.p>
          </motion.div>
        </div>

        {/* Admin Access Button */}
        <motion.button initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3,
        duration: 0.4
      }} whileTap={{
        scale: 0.98
      }} onClick={switchToAdmin} className="w-full bg-orange-600 hover:bg-orange-700 text-white rounded-xl p-4 flex items-center justify-between transition-colors shadow-lg shadow-orange-600/30" data-id="element-424">
          <div className="flex items-center gap-3" data-id="element-425">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center" data-id="element-426">
              <ShieldIcon className="w-5 h-5" data-id="element-427" />
            </div>
            <div className="text-left" data-id="element-428">
              <p className="font-bold" data-id="element-429">Switch to Admin</p>
              <p className="text-xs text-orange-100" data-id="element-430">Access admin dashboard</p>
            </div>
          </div>
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-431">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" data-id="element-432" />
          </svg>
        </motion.button>
      </motion.div>

      {/* Content */}
      <div className="p-6 pb-24" data-id="element-433">
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3,
        duration: 0.4
      }} className="flex items-center justify-between mb-6" data-id="element-434">
          <h2 className="text-xl font-bold text-slate-900" data-id="element-435">Your Jobs</h2>
          <motion.div whileTap={{
          scale: 0.95
        }} data-id="element-436">
            <Button variant="primary" size="sm" onClick={() => navigate('/request-job')} className="flex items-center gap-2" data-id="element-437">
              <PlusIcon className="w-4 h-4" data-id="element-438" />
              New Job
            </Button>
          </motion.div>
        </motion.div>

        {myJobs.length === 0 ? <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4,
        duration: 0.5
      }} className="text-center py-12" data-id="element-439">
            <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} transition={{
          delay: 0.5,
          type: 'spring',
          stiffness: 200,
          damping: 15
        }} className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-440">
              <ZapIcon className="w-8 h-8 text-orange-600" data-id="element-441" />
            </motion.div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2" data-id="element-442">
              Ready to blast?
            </h3>
            <p className="text-slate-500 mb-6" data-id="element-443">
              Request your first sandblasting job to get started
            </p>
            <motion.div whileTap={{
          scale: 0.95
        }} data-id="element-444">
              <Button variant="primary" onClick={() => navigate('/request-job')} data-id="element-445">
                Request a Job
              </Button>
            </motion.div>
          </motion.div> : <div className="space-y-4" data-id="element-446">
            {myJobs.map((job, index) => <motion.div key={job.id} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4 + index * 0.1,
          duration: 0.4,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-447">
                <JobCard job={job} onClick={() => navigate(`/job/${job.id}`)} data-id="element-448" />
              </motion.div>)}
          </div>}
      </div>

      {/* Bottom Navigation */}
      <motion.div initial={{
      y: 100
    }} animate={{
      y: 0
    }} transition={{
      delay: 0.5,
      duration: 0.5,
      ease: [0.22, 1, 0.36, 1]
    }} className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-slate-200 px-6 py-4 safe-area-bottom" data-id="element-449">
        <div className="flex items-center justify-around max-w-md mx-auto" data-id="element-450">
          <button className="flex flex-col items-center gap-1 text-orange-600 relative" data-id="element-451">
            <motion.div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center" whileTap={{
            scale: 0.9
          }} data-id="element-452">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" data-id="element-453">
                <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" data-id="element-454" />
              </svg>
            </motion.div>
            <span className="text-xs font-semibold" data-id="element-455">Home</span>
            <motion.div layoutId="activeTab" className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-orange-600 rounded-full" data-id="element-456" />
          </button>
          <motion.button whileTap={{
          scale: 0.9
        }} onClick={() => navigate('/request-job')} className="flex flex-col items-center gap-1 text-slate-400" data-id="element-457">
            <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center" data-id="element-458">
              <PlusIcon className="w-5 h-5" data-id="element-459" />
            </div>
            <span className="text-xs font-semibold" data-id="element-460">New Job</span>
          </motion.button>
          <motion.button whileTap={{
          scale: 0.9
        }} onClick={() => navigate('/profile')} className="flex flex-col items-center gap-1 text-slate-400" data-id="element-461">
            <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center" data-id="element-462">
              <UserIcon className="w-5 h-5" data-id="element-463" />
            </div>
            <span className="text-xs font-semibold" data-id="element-464">Profile</span>
          </motion.button>
        </div>
      </motion.div>
    </div>;
}